// GA4 ecommerce contract for the pre-checkout funnel.
// Source of truth: wanderu/strategy engineering/initiatives/ga4-ecommerce-remarketing/
// sub-initiatives/event-contract.md. Values here are load-bearing downstream:
// the sGTM margin template branches on `item_category`, and Google Ads reads
// `google_business_vertical` off the items.
//
// The contract's other `item_category`, `insurance`, has no emitter here by
// design: insurance is a checkout step, not a Trip Summary add-on, and
// `purchase` already reports it as its own event.

// Where a Book click sends the traveler: a partner deep link hands them to the
// carrier's own site, everything else stays on a Wanderu checkout. Split out so
// conversion-rate analyses are not comparing the two as one funnel.
const CHECKOUT_DESTINATION_CARRIER = 'carrier';
const CHECKOUT_DESTINATION_WANDERU = 'wanderu';

const CURRENCY_USD = 'USD';

const GOOGLE_BUSINESS_VERTICAL = 'travel';

const ITEM_CATEGORY_TICKET = 'ticket';

const ITEM_LIST_ID_SEARCH_RESULTS_DEPART = 'search_results_depart';
const ITEM_LIST_ID_SEARCH_RESULTS_RETURN = 'search_results_return';

const ITEM_LIST_NAME_SEARCH_RESULTS_DEPART = 'Search results (depart)';
const ITEM_LIST_NAME_SEARCH_RESULTS_RETURN = 'Search results (return)';

// GA4 reads the list id and the list name as one dimension, so they are paired
// here rather than passed separately by call sites -- nothing can send a name
// that disagrees with its id. One constant name per list: route, query and
// filter detail belong in the items, never in the name.
const ITEM_LIST_NAMES: Record<string, string> = {
    [ITEM_LIST_ID_SEARCH_RESULTS_DEPART]: ITEM_LIST_NAME_SEARCH_RESULTS_DEPART,
    [ITEM_LIST_ID_SEARCH_RESULTS_RETURN]: ITEM_LIST_NAME_SEARCH_RESULTS_RETURN,
};

const LEG_ROLE_DEPART = 'depart';
const LEG_ROLE_RETURN = 'return';

const MATCH_TYPE_DIRECT = 'direct';
const MATCH_TYPE_MIXED = 'mixed';
const MATCH_TYPE_NEARBY = 'nearby';

const MODE_MIXED = 'mixed';

// The `search` event (ui-react#1222). Not an ecommerce event -- it carries the
// search itself, not what came back -- but it rides the same dataLayer to the
// same container, and `view_item_list` is what it is read against.
const SEARCH_ENTRY_POINT_CLASSIC_HANDOFF = 'classic_handoff';
const SEARCH_ENTRY_POINT_DATE_SUGGESTION = 'date_suggestion';
const SEARCH_ENTRY_POINT_DEEP_LINK = 'deep_link';
const SEARCH_ENTRY_POINT_SEARCH_FORM = 'search_form';

// GA4 truncates an event parameter value at 100 characters; cutting it here
// keeps what GA4 and TikTok's `query` receive identical.
const SEARCH_TERM_MAX_LENGTH = 100;

// Web searches every mode, so there is no mode on the search; blog search will
// be the same event with its own type.
const SEARCH_TYPE_TRIP = 'trip';

export {
    CHECKOUT_DESTINATION_CARRIER,
    CHECKOUT_DESTINATION_WANDERU,
    CURRENCY_USD,
    GOOGLE_BUSINESS_VERTICAL,
    ITEM_CATEGORY_TICKET,
    ITEM_LIST_ID_SEARCH_RESULTS_DEPART,
    ITEM_LIST_ID_SEARCH_RESULTS_RETURN,
    ITEM_LIST_NAMES,
    ITEM_LIST_NAME_SEARCH_RESULTS_DEPART,
    ITEM_LIST_NAME_SEARCH_RESULTS_RETURN,
    LEG_ROLE_DEPART,
    LEG_ROLE_RETURN,
    MATCH_TYPE_DIRECT,
    MATCH_TYPE_MIXED,
    MATCH_TYPE_NEARBY,
    MODE_MIXED,
    SEARCH_ENTRY_POINT_CLASSIC_HANDOFF,
    SEARCH_ENTRY_POINT_DATE_SUGGESTION,
    SEARCH_ENTRY_POINT_DEEP_LINK,
    SEARCH_ENTRY_POINT_SEARCH_FORM,
    SEARCH_TERM_MAX_LENGTH,
    SEARCH_TYPE_TRIP,
};
